/**
 * Readable text of an HTML email. Drops what is never shown (<head>, <style>,
 * <script>, comments) before removing tags — otherwise HTML-only emails
 * (e.g. AccuLynx) show their CSS as the message.
 */

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  copy: "©",
  reg: "®",
  trade: "™",
  zwnj: "",
  zwj: "",
};

const decodeEntities = (value: string) =>
  value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === "#") {
      const point =
        code[1] === "x" || code[1] === "X"
          ? parseInt(code.slice(2), 16)
          : parseInt(code.slice(1), 10);
      return Number.isFinite(point) && point > 0 && point < 0x110000
        ? String.fromCodePoint(point)
        : match;
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? match;
  });

/** Removes the parts of an email that are never displayed. */
export const stripInvisibleHtml = (html: string) =>
  html
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<head\b[\s\S]*?<\/head\s*>/gi, " ")
    .replace(
      /<(style|script|title|noscript|template)\b[\s\S]*?<\/\1\s*>/gi,
      " ",
    )
    // An unclosed <style> (broken email) hides everything after it anyway.
    .replace(/<(style|script)\b[\s\S]*$/i, " ");

/** Visible text on one line (previews, lengths, ticket body). */
export const htmlToVisibleText = (html: string) =>
  decodeEntities(
    stripInvisibleHtml(html)
      .replace(/<[^>]+>/g, " ")
      // Zero-width and preheader padding characters.
      .replace(/[​-‍⁠﻿͏­]/g, ""),
  )
    .replace(/\s+/g, " ")
    .trim();
